import { Injectable, inject } from '@angular/core';

import moment from 'moment';

import { Constants } from 'app/constants/constants';
import { Dashboard, Widget } from 'app/providers/bridge-api/dashboard.service';
import { FieldService } from 'app/providers/storage-api/field.service';
import { EnableClassTelemetry } from 'app/services/otel.service';
import { TimezoneService } from 'app/services/timezone.service';
import {
  BreakdownOptions,
  WidgetChartOptions,
} from '../components/widget-chart/widget-chart.component';

import { AbstractChartReport } from './chart-report/chart-report-abstract/chart-report-abstract';
import { DonutChartReportUtils } from './chart-report/chart-report-donut/chart-report-donut';
import { GaugeChartReportUtils } from './chart-report/chart-report-gauge/chart-report-gauge';

export interface TableOptions {
  [key: string]: any;
}

@EnableClassTelemetry()
@Injectable()
export class ChartTableService {
  private timezoneService = inject(TimezoneService);

  readonly DATE_INTERVAL_FORMATS = {
    minute: 'lll',
    hour: 'lll',
    day: 'll',
    week: 'll (gg)',
    month: 'MMM YYYY',
    quarter: "[Q]Q'YY",
    year: 'YYYY',
  };

  public tranformToDataSource(
    dashboard: Dashboard,
    widget: Widget<WidgetChartOptions>,
    response: any,
    options: {},
    report: AbstractChartReport,
    missing: any,
    previousMissing: any,
    comparisonMissings: Map<number, number>
  ): TableOptions | void {
    let func: (
      dashboard: Dashboard,
      widget: Widget<WidgetChartOptions>,
      response: any,
      options: {},
      report: AbstractChartReport,
      missing: any,
      previousMissing: any,
      comparisonMissings: Map<number, number>
    ) => TableOptions;

    switch (widget.payload.type) {
      case 'featureScoring':
      case 'wordcloud':
        func = this.configForWordCloudTable;
        break;
      case 'powerScore':
      case 'powerScoreOverTime':
      case 'sentimentScore':
        if (widget.payload.analyticItem === 'documents') {
          func = this.configForSentimentDocumentsOverTimeTable;
        } else if (response.items.count) {
          func = this.configForSentimentScoreTable;
        } else {
          func = this.configForSentimentScoreOverTimeTable;
        }
        break;
      case 'sentimentVolume':
        func = this.configForSentimentVolumeOverTimeTable;
        break;
      case 'sentimentPolarity':
        if (
          widget.payload.analyticItem === 'documents' &&
          (widget.payload.aggregator === 'time' || widget.payload.aggregator === 'percentage_time')
        ) {
          func = this.configForSentimentPolarityDocumentsOverTimeTable;
        } else {
          func = this.configForSentimentPolarityTable;
        }
        break;
      case 'documentCount':
        if (response.currentPeriodData) {
          func = this.configForDocumentCountCPPP;
        } else if (response.comparisonBuckets && !response.reportName.includes('OverTime')) {
          func = this.configForDocumentCountComparison;
        } else if (response.reportName === 'featureTopThemes') {
          func = this.configForFeatureTopThemes;
        } else if (response.reportName === 'featurePhrases') {
          func = this.configForFeaturePhrases;
        } else if (response.items.count) {
          func = this.configForDocumentCountTable;
        } else {
          func = this.configForDocumentCountOverTimeTable;
        }
        break;
      case 'taxonomyMap':
        func = this.configForTaxonomyMap;
        break;
      case 'numericMetadata':
        if (response.comparisonBuckets?.length > 1) {
          func = this.configForNumericMetadataOverTimeTableComparison;
        } else {
          func = this.configForNumericMetadataOverTimeTable;
        }
        break;
      case 'scatterPlot':
        func = this.configForScatterPlotTable;
        break;
      case 'breakdown':
        func = this.configForBreakdownTable;
        break;
      case 'donut':
        func = this.configForDonutTable;
        break;
      case 'gauge':
        func = this.configForGaugeTable;
        break;
    }

    if (!func) {
      return;
    }

    return func.bind(this)(
      dashboard,
      widget,
      response,
      options,
      report,
      missing,
      previousMissing,
      comparisonMissings
    );
  }

  // {
  //   "generatedAt": "2019-05-03T09:25:47-0400",
  //   "nextPageToken": null,
  //   "reportName": "featureTopThemes",
  //   "items": {
  //       "great place": {
  //           "neutral": 2,
  //           "positive": 2
  //       },
  //       "enjoyable part": {
  //           "positive": 3
  //       },

  public configForFeatureTopThemes(
    dashboard: Dashboard,
    widget: Widget<WidgetChartOptions>,
    response: any,
    _options: {}
  ): TableOptions {
    const result = {
      columns: ['name', 'count'],
      rows: [],
      options: {
        numericSuffix: '',
      },
    };

    let suffix = '';
    if (widget.payload.aggregator === 'percentage') {
      suffix = '_pct';
    }

    this.pushSentimentColumns(result, dashboard);

    for (const name of Object.getOwnPropertyNames(response.items)) {
      const container = response.items[name];
      const row = { name, count: 0 };
      result.columns.slice(2).forEach(function (key) {
        row[key + suffix] = 0;
      });

      for (const key of Object.getOwnPropertyNames(container)) {
        row[key.replace(' ', '_') + suffix] = container[key];
        row.count += container[key];
      }

      if (widget.payload.aggregator === 'percentage') {
        result.columns.slice(2).forEach((key) => {
          row[key + suffix] = row[key + suffix] / row.count;
        });
        row['percentage'] = row.count / response.totalHits;
      }

      result.rows.push(row);
    }

    result.rows.sort((a, b) => b.count - a.count);

    return result;
  }

  // {
  //   "totalHits": 1999,
  //   "generatedAt": "2024-06-10T15:41:48+0200",
  //   "generatedElapsedMillis": 50,
  //   "nextPageToken": null,
  //   "reportName": "featurePhrases",
  //   "items": {
  //     "sentiment": {
  //       "great": 0.74134947703125,
  //       "good": 0.74425000625,
  //       "professional": 0.6953216475
  //     },
  //     "count": {
  //       "great": 64,
  //       "good": 16,
  //       "professional": 16
  //     }
  //   },
  //   "extra": null
  // }
  public configForFeaturePhrases(
    _dashboard: Dashboard,
    widget: Widget<WidgetChartOptions>,
    response: any,
    _options: {}
  ): TableOptions {
    const result = {
      columns: ['name', 'count', 'sentiment'],
      rows: [],
      options: {
        numericSuffix: '',
      },
    };

    for (const name of Object.getOwnPropertyNames(response.items.count)) {
      const row = {
        name,
        count: response.items.count[name],
        sentiment: response.items.sentiment[name],
      };

      if (widget.payload.aggregator === 'percentage') {
        row['percentage'] = row.count / response.totalHits;
      }

      result.rows.push(row);
    }

    result.rows.sort((a, b) => b.count - a.count);

    return result;
  }

  // {
  //   "items": {
  //     "count": {
  //       "£10": {
  //           "sentimentScore": 0.09999999999999999,
  //           "documentCount": 3
  //       },

  public configForWordCloudTable(
    _dashboard: Dashboard,
    _widget: Widget<WidgetChartOptions>,
    response: any,
    _options: {}
  ): TableOptions {
    const result = {
      columns: ['name', 'sentiment', 'count'],
      rows: [],
      options: {
        numericSuffix: '',
      },
    };

    for (const name of Object.getOwnPropertyNames(response.items.count)) {
      const container = response.items.count[name];
      const value = container.documentCount;
      const displayName = this.extractTaxonomyDisplayName(response, name);
      const row = {
        name: displayName.displayName,
        count: value,
        sentiment: container.sentimentScore,
      };
      displayName.taxonomyName && (row['taxonomyName'] = displayName.taxonomyName);
      result.rows.push(row);
    }

    result.rows.sort((a, b) => b.count - a.count);

    return result;
  }

  public configForTaxonomyMap(
    _dashboard: Dashboard,
    widget: Widget<WidgetChartOptions>,
    response: any,
    _options: {}
  ): TableOptions {
    let columnNames = ['name', 'count', 'sentiment'];
    if (response.reportName === 'featureTopThemes') {
      let sentimentValues = ['positive', 'neutral', 'negative'];
      if (widget.payload.sentimentScale === 5) {
        sentimentValues = [
          'very positive',
          'somewhat positive',
          'neutral',
          'very negative',
          'somewhat negative',
        ];
      }
      columnNames = ['name', 'count'];
      const allColumns = columnNames.concat(sentimentValues);
      columnNames = allColumns;
    }
    const result = {
      columns: columnNames,
      rows: [],
      options: {
        numericSuffix: '',
      },
    };

    if (response.items.hasOwnProperty('root')) {
      for (const name of Object.getOwnPropertyNames(response.items.root)) {
        const container = response.items.root[name];
        const value = container.count;
        const score = container.sentimentScore;
        const displayName = response.items.root[name]['path']
          ? response.items.root[name]['path'] + ' | ' + response.items.root[name]['name']
          : response.items.root[name]['name'];
        const row = { name: displayName, count: value, sentiment: null };
        if (score !== null && score !== 'Infinity' && score !== 'NaN') {
          row.sentiment = score;
        }
        result.rows.push(row);
      }
    } else if (response.reportName === 'featureTopThemes') {
      for (const name of Object.getOwnPropertyNames(response.items)) {
        let valueCount = 0;
        const row = { name };
        for (const childId of Object.getOwnPropertyNames(response.items[name])) {
          valueCount += response.items[name][childId];
          row[childId] = response.items[name][childId];
        }
        row['count'] = valueCount;
        result.rows.push(row);
      }
    } else {
      let item_rows = ['count'];
      if (
        response.reportName === 'featureTopMentions' ||
        response.reportName === 'featurePhrases'
      ) {
        item_rows = ['topic_count', 'entity_count'];
      }
      for (const item_type of item_rows) {
        if (response.items[item_type]) {
          const valueRow = response.items[item_type];
          for (const name of Object.getOwnPropertyNames(valueRow)) {
            const container = response.items[item_type][name];
            const value = container;
            const score = response.items.sentiment?.[name] || 0;
            const row = { name, count: value, sentiment: score };
            result.rows.push(row);
          }
        }
      }
    }

    result.rows.sort((a, b) => b.count - a.count);

    return result;
  }

  // "items": {
  //   "2013-09-03T00:00:00.000Z": {
  //     "value": 0.15389044286927592
  //   },
  //   "2013-09-04T00:00:00.000Z": {
  //     "value": 0.20931379146050408
  //   },

  public configForSentimentDocumentsOverTimeTable(
    _dashboard: Dashboard,
    widget: Widget<WidgetChartOptions>,
    response: any,
    _options: {}
  ): TableOptions {
    if (!widget.payload.dateInterval) {
      return;
    }

    const dateColumn = widget.payload.dateInterval.toString();
    const result = {
      columns: [dateColumn, 'count', 'sentiment'],
      rows: [],
      options: {
        numericSuffix: '',
      },
    };

    for (const ts of Object.getOwnPropertyNames(response.items)) {
      const container = response.items[ts];

      const row = {
        [dateColumn]: this.formatDateColumn(ts, dateColumn),
        count: container.documentCount,
        sentiment: container.value,
      };

      result.rows.push(row);
    }

    result.rows.sort((a, b) => moment(b[dateColumn]).diff(moment(a[dateColumn])));

    return result;
  }

  public configForSentimentPolarityDocumentsOverTimeTable(
    dashboard: Dashboard,
    widget: Widget<WidgetChartOptions>,
    response: any,
    _options: {}
  ): TableOptions {
    if (!widget.payload.dateInterval) {
      return;
    }

    const dateColumn = widget.payload.dateInterval.toString();

    const result = {
      columns: [dateColumn],
      rows: [],
      options: {
        numericSuffix: '',
      },
    };

    this.pushSentimentColumns(result, dashboard);

    if (widget.payload.aggregator === 'percentage_time') {
      this.pushSentimentColumns(result, dashboard, '_pct');
    }

    for (const ts of Object.getOwnPropertyNames(response.items)) {
      const container = response.items[ts];
      const row = {};
      row[dateColumn] = this.formatDateColumn(ts, dateColumn);

      const total = Object.values(container as { [key: string]: number }).reduce(
        (sum, current) => sum + current,
        0
      );

      let i = 1;
      for (const column of result.columns.slice(1)) {
        const key = column.replace('_pct', '').replace('_', ' ');
        const value = (container[key] || 0).toFixed(0);

        if (column.endsWith('_pct')) {
          row[result.columns[i]] = value / total;
        } else {
          row[result.columns[i]] = value;
        }

        i++;
      }
      row['count'] = total;
      result.rows.push(row);
    }

    result.columns.push('count');

    result.rows.sort((a, b) => moment(b[dateColumn]).diff(moment(a[dateColumn])));

    return result;
  }

  // {
  //   "items": {
  //     "2013-09-03T00:00:00.000Z": {
  //       "£10": {
  //         "sentimentScore": 0.09999999999999999,
  //         "documentCount": 3
  //       },
  //       "manager": {
  //         "sentimentScore": 1.876,
  //         "documentCount": 1
  //       }
  //     },

  public configForSentimentScoreOverTimeTable(
    _dashboard: Dashboard,
    widget: Widget<WidgetChartOptions>,
    response: any,
    _options: {}
  ): TableOptions {
    if (!widget.payload.dateInterval) {
      return;
    }

    const dateColumn = widget.payload.dateInterval.toString();
    const result = {
      columns: [dateColumn, 'name', 'count', 'sentiment'],
      rows: [],
      options: {
        numericSuffix: '',
      },
    };

    for (const ts of Object.getOwnPropertyNames(response.items)) {
      const container = response.items[ts];
      for (const name of Object.getOwnPropertyNames(container)) {
        const count = container[name].documentCount;
        const sentiment = container[name].sentimentScore;
        const displayName = this.extractTaxonomyDisplayName(response, name);
        const row = {
          [dateColumn]: this.formatDateColumn(ts, dateColumn),
          name: displayName.displayName,
          count,
          sentiment,
          item: name,
        };
        displayName.taxonomyName && (row['taxonomyName'] = displayName.taxonomyName);
        result.rows.push(row);
      }
    }

    result.rows.sort(
      (a, b) => moment(b[dateColumn]).diff(moment(a[dateColumn])) || b.sentiment - a.sentiment
    );

    return result;
  }

  // {
  //   "items": {
  //     "count": {
  //       "£10": {
  //         "sentimentScore": 0.09999999999999999,
  //         "documentCount": 3
  //       },
  //       "manager": {
  //         "sentimentScore": -0.050321419999999985,
  //         "documentCount": 7
  //       },
  public configForSentimentScoreTable(
    _dashboard: Dashboard,
    _widget: Widget<WidgetChartOptions>,
    response: any,
    _options: {}
  ): TableOptions {
    const result = {
      columns: ['name', 'count', 'sentiment'],
      rows: [],
      options: {
        numericSuffix: '',
      },
    };

    for (const name of Object.getOwnPropertyNames(response.items.count)) {
      const count = response.items.count[name].documentCount || response.items.docCount[name] || 0;
      const sentiment =
        response.items.count[name].sentimentScore || response.items.count[name] || 0.0;
      const displayName = this.extractTaxonomyDisplayName(response, name);
      const row = {
        name: displayName.displayName,
        count,
        sentiment,
        item: name,
      };
      displayName.taxonomyName && (row['taxonomyName'] = displayName.taxonomyName);
      result.rows.push(row);
    }

    result.rows.sort((a, b) => b.sentiment - a.sentiment);

    return result;
  }

  public configForDocumentCountOverTimeTable(
    _dashboard: Dashboard,
    widget: Widget<WidgetChartOptions>,
    response: any,
    _options: {}
  ): TableOptions {
    if (!widget.payload.dateInterval) {
      return;
    }

    const dateColumn = widget.payload.dateInterval.toString();

    const columns: string[] = [dateColumn, 'name'];
    if (widget.payload.aggregator === 'percentage_time') {
      columns.push('count_pct');
    }
    columns.push('count');

    const result = {
      columns,
      rows: [],
      options: {
        numericSuffix: '',
      },
    };

    for (const ts of Object.getOwnPropertyNames(response.items)) {
      const container = response.items[ts]['count']
        ? response.items[ts]['count']
        : response.items[ts];

      const totalDocumentCountByDate = container.documentCount;

      for (const name of Object.getOwnPropertyNames(container).filter(
        (n) => n !== 'documentCount'
      )) {
        const displayName = this.extractTaxonomyDisplayName(response, name);

        const containerItem = container[name];
        const value =
          typeof containerItem === 'object' ? containerItem.documentCount : containerItem;

        const row = {
          name: displayName.displayName,
          item: name,
          count: value,
        };
        row[dateColumn] = this.formatDateColumn(ts, dateColumn);

        if (widget.payload.aggregator === 'percentage_time') {
          row['percentage'] = value / totalDocumentCountByDate;
        }

        displayName.taxonomyName && (row['taxonomyName'] = displayName.taxonomyName);

        result.rows.push(row);
      }
    }

    result.rows.sort((a, b) => moment(b[dateColumn]).diff(moment(a[dateColumn])));

    return result;
  }

  public configForDocumentCountCPPP(
    _dashboard: Dashboard,
    widget: Widget<WidgetChartOptions>,
    response: any,
    _options: { [key: string]: any },
    report: AbstractChartReport,
    missing: any,
    previousMissing: any
  ): TableOptions {
    const result = {
      columns: ['name', 'count_current', 'count_previous'],
      rows: [],
      options: {
        numericSuffix: '',
      },
    };

    let currentPeriodData;
    let previousPeriodData;
    if (response.reportName === 'featureTopThemes') {
      const sumItemSubValues = (items) => {
        const data = {};
        for (const key of Object.getOwnPropertyNames(items)) {
          let value = 0;
          for (const subkey of Object.getOwnPropertyNames(items[key])) {
            value += items[key][subkey];
          }
          data[key] = value;
        }
        return data;
      };

      currentPeriodData = sumItemSubValues(response.currentPeriodData.items);
      previousPeriodData = sumItemSubValues(response.items);
    } else {
      currentPeriodData = response.currentPeriodData.items.count;
      previousPeriodData = response.items.count;
    }

    const getItemDisplayName = (key) => {
      let name = key;
      if (response.reportName === 'taxonomyTopFeatures') {
        const taxonomyNodes =
          response.currentPeriodData?.extra['taxonomyNodes'] ?? response.extra['taxonomyNodes'];
        name = taxonomyNodes[key].path
          ? taxonomyNodes[key].path + ' | ' + taxonomyNodes[key].name
          : taxonomyNodes[key].name;
      }
      return name;
    };
    for (const [key, value] of Object.entries(currentPeriodData)) {
      const row: any = {
        name: getItemDisplayName(key),
        currentCount: value,
        previousCount: previousPeriodData[key] ?? 0,
      };

      if (widget.payload.aggregator === 'percentage') {
        row.currentPercentage = row.currentCount / report.options.currentPeriodDocumentCount;
        row.previousPercentage = row.previousCount / report.options.previousPeriodDocumentCount;
      }

      result.rows.push(row);
    }

    if (widget.payload.showBlanks && report.options.filteredDocumentCount) {
      const blankRow: any = {
        name: Constants.BLANK,
        currentCount: missing,
        previousCount: previousMissing,
      };
      if (widget.payload.aggregator === 'percentage') {
        blankRow.currentPercentage =
          blankRow.currentCount / report.options.currentPeriodDocumentCount;
        blankRow.previousPercentage =
          blankRow.previousCount / report.options.previousPeriodDocumentCount;
      }
      result.rows.push(blankRow);
    }

    result.rows.sort((a, b) => b.currentCount - a.currentCount);

    return result;
  }

  public configForDocumentCountComparison(
    _dashboard: Dashboard,
    widget: Widget<WidgetChartOptions>,
    response: any,
    _options: { [key: string]: any },
    report: AbstractChartReport,
    _missing: any,
    _previousMissing: any,
    comparisonMissings: Map<number, number>
  ): TableOptions {
    const result = {
      columns: ['name'],
      rows: [],
      options: {
        numericSuffix: '',
      },
    };

    const bucketsData = [];
    if (response.reportName === 'featureTopThemes') {
      const sumItemSubValues = (items) => {
        const data = {};
        for (const key of Object.getOwnPropertyNames(items)) {
          let value = 0;
          for (const subkey of Object.getOwnPropertyNames(items[key])) {
            value += items[key][subkey];
          }
          data[key] = value;
        }
        return data;
      };

      for (const bucket of response.comparisonBuckets) {
        bucketsData.push(sumItemSubValues(bucket.items));
      }
    } else {
      for (const bucket of response.comparisonBuckets) {
        bucketsData.push(bucket.items.count);
      }
    }

    for (const [idx, bucket] of bucketsData.entries()) {
      result.columns.push(`count_bucket_${idx}`);
      for (const [key, value] of Object.entries(bucket)) {
        const displayName = this.extractTaxonomyDisplayName(response, key);
        let row = result.rows.find((r) => r.item === key);
        if (!row) {
          row = {
            name: displayName.displayName,
            buckets: [],
            item: key,
          };
          result.rows.push(row);
        }

        row.buckets[idx] = { count: value };

        displayName.taxonomyName && (row['taxonomyName'] = displayName.taxonomyName);

        if (widget.payload.aggregator === 'percentage') {
          row.buckets[idx].percentage =
            row.buckets[idx].count / report.options.comparisonBucketsCounts[idx];
        }
      }
    }

    if (widget.payload.showBlanks && report.options.comparisonBucketsCounts?.[0]) {
      const blankRow: any = {
        name: Constants.BLANK,
        buckets: [],
        item: Constants.BLANK,
      };

      comparisonMissings.forEach((v, k) => {
        blankRow.buckets[k] = { count: v };

        if (widget.payload.aggregator === 'percentage') {
          blankRow.buckets[k].percentage = v / report.options.comparisonBucketsCounts[k];
        }
      });

      result.rows.push(blankRow);
    }

    result.rows.sort((a, b) => {
      for (let i = 0; i < Math.min(a.buckets.length, b.buckets.length); i++) {
        const countA = a.buckets[i]?.count ?? 0;
        const countB = b.buckets[i]?.count ?? 0;
        if (countA !== countB) {
          return countB - countA;
        }
      }
      return b.buckets.length - a.buckets.length;
    });

    return result;
  }

  public configForDocumentCountTable(
    _dashboard: Dashboard,
    widget: Widget<WidgetChartOptions>,
    response: any,
    options: { [key: string]: any },
    report: AbstractChartReport,
    missing: any
  ): TableOptions {
    const result = {
      columns: ['name', 'count'],
      rows: [],
      options: {
        numericSuffix: '',
      },
    };

    for (const name of Object.getOwnPropertyNames(response.items.count)) {
      const value = response.items.count[name];
      const displayName = this.extractTaxonomyDisplayName(response, name);
      const row: any = {
        name: displayName.displayName,
        count: value,
        item: name,
      };

      displayName.taxonomyName && (row['taxonomyName'] = displayName.taxonomyName);

      if (widget.payload.aggregator === 'percentage') {
        row.percentage = row.count / report.options.filteredDocumentCount;
      }

      result.rows.push(row);
    }

    if (
      widget.payload.showBlanks &&
      report.options.filteredDocumentCount &&
      options?.analyticItemFieldType !== 'keyword'
    ) {
      const blankRow: any = {
        name: Constants.BLANK,
        count: missing,
        item: Constants.BLANK,
      };
      if (widget.payload.aggregator === 'percentage') {
        blankRow.percentage = blankRow.count / report.options.filteredDocumentCount;
      }
      result.rows.push(blankRow);
    }

    result.rows.sort((a, b) => b.count - a.count);

    return result;
  }

  // "items": {
  //   "2013-09-03T00:00:00.000Z": {
  //       "negative": 0.14979757085020243,
  //       "neutral": 0.6072874493927125,
  //       "positive": 0.242914979757085,
  //       "total": 247
  //   },

  public configForSentimentVolumeOverTimeTable(
    dashboard: Dashboard,
    widget: Widget<WidgetChartOptions>,
    response: any,
    _options: {}
  ): TableOptions {
    if (!widget.payload.dateInterval) {
      return;
    }

    const dateColumn = widget.payload.dateInterval.toString();
    const result = {
      columns: [dateColumn],
      rows: [],
      options: {
        numericSuffix: '',
      },
    };

    this.pushSentimentColumns(result, dashboard, '_pct');

    for (const ts of Object.getOwnPropertyNames(response.items)) {
      const container = response.items[ts];
      const row = {
        [dateColumn]: this.formatDateColumn(ts, dateColumn),
      };

      let i = 1;
      for (const column of result.columns.slice(1)) {
        const key = column.replace('_pct', '').replace('_', ' ');
        row[result.columns[i]] = container[key] || 0;
        i++;
      }
      row['count'] = container.total;
      result.rows.push(row);
    }

    result.columns.push('count');

    result.rows.sort((a, b) => moment(b[dateColumn]).diff(moment(a[dateColumn])));

    return result;
  }

  public configForSentimentPolarityTable(
    dashboard: Dashboard,
    widget: Widget<WidgetChartOptions>,
    response: any,
    _options: {}
  ): TableOptions {
    const result = {
      columns: ['name'],
      rows: [],
      options: {
        numericSuffix: '',
      },
    };

    this.pushSentimentColumns(result, dashboard);

    if (widget.payload.aggregator === 'percentage') {
      result.options.numericSuffix = '%';
    }

    for (const name of Object.getOwnPropertyNames(response.items.count)) {
      const value = response.items.count[name];

      const displayName = this.extractTaxonomyDisplayName(response, name);
      const row = {
        name: displayName.displayName,
        item: name,
      };
      displayName.taxonomyName && (row['taxonomyName'] = displayName.taxonomyName);

      if (widget.payload.aggregator === 'percentage') {
        let tot = 0;
        for (const key of Object.getOwnPropertyNames(value)) {
          tot += value[key];
        }

        if (tot > 0) {
          for (const key of Object.getOwnPropertyNames(value)) {
            value[key] = value[key] / tot;
          }
        }
        row['count'] = tot;
      }

      let i = 1;
      let total = 0;
      for (const column of result.columns.slice(1)) {
        const key = column.replace('_', ' ');
        let resultKey = result.columns[i];
        if (widget.payload.aggregator === 'percentage') {
          resultKey = `${resultKey}_pct`;
        }
        row[resultKey] = value[key] || 0;
        total += value[key] || 0;
        i++;
      }
      row['count'] ??= total;

      result.rows.push(row);
    }

    // sort desc based on total
    result.rows.sort((a, b) => b.total - a.total);

    return result;
  }

  public configForNumericMetadataOverTimeTable(
    _dashboard: Dashboard,
    widget: Widget<WidgetChartOptions>,
    response: any,
    _options: {}
  ): TableOptions {
    if (!widget.payload.dateInterval) {
      return;
    }

    const dateColumn = widget.payload.dateInterval.toString();
    const result = {
      columns: [dateColumn, 'metadata', 'count'],
      rows: [],
      options: {
        numericSuffix: '',
      },
    };

    if (
      widget.payload.numericMetadataFields.length > 1 ||
      widget.payload.numericMetadataFields[0]?.fieldId !== 'docs'
    ) {
      result.columns.push('score');
    }

    for (
      let fieldIndex = 0;
      fieldIndex < widget.payload.numericMetadataFields.length;
      fieldIndex++
    ) {
      const numericMetadataField = widget.payload.numericMetadataFields[fieldIndex];

      for (const date of Object.getOwnPropertyNames(response.items)) {
        const values = response.items[date].metadata[fieldIndex];

        let score;
        switch (numericMetadataField.scoring) {
          case 'top':
            score = values.topScore;
            break;
          case 'bottom':
            score = values.bottomScore;
            break;
          default:
            score = values.score;
            break;
        }

        if (score !== null && score !== 'Infinity' && score !== 'NaN') {
          const row = {
            metadata: numericMetadataField.label,
            count: values.docs,
            [dateColumn]: this.formatDateColumn(date, dateColumn),
          };
          if (numericMetadataField.fieldId !== 'docs') {
            row.score = score;
          }
          result.rows.push(row);
        }
      }
    }

    result.rows.sort(
      (a, b) => moment(b[dateColumn]).diff(moment(a[dateColumn])) || b.score - a.score
    );

    return result;
  }

  public configForNumericMetadataOverTimeTableComparison(
    _dashboard: Dashboard,
    widget: Widget<WidgetChartOptions>,
    response: any,
    _options: {}
  ): TableOptions {
    if (!widget.payload.dateInterval) {
      return;
    }

    const dateColumn = widget.payload.dateInterval.toString();
    const result = {
      columns: [dateColumn, 'metadata'],
      rows: [],
      options: {
        numericSuffix: '',
      },
    };

    const isOnlyDocs =
      widget.payload.numericMetadataFields.length === 1 &&
      widget.payload.numericMetadataFields[0].fieldId === 'docs';

    const map = new Map<
      string,
      { metadata: string; buckets: { count: number; score: number }[] }
    >();

    for (
      let fieldIndex = 0;
      fieldIndex < widget.payload.numericMetadataFields.length;
      fieldIndex++
    ) {
      const numericMetadataField = widget.payload.numericMetadataFields[fieldIndex];

      for (
        let comparisonBucketIndex = 0;
        comparisonBucketIndex < response.comparisonBuckets.length;
        comparisonBucketIndex++
      ) {
        for (const date of Object.getOwnPropertyNames(
          response.comparisonBuckets[comparisonBucketIndex].items
        )) {
          const values =
            response.comparisonBuckets[comparisonBucketIndex].items[date].metadata[fieldIndex];

          let score;
          switch (numericMetadataField.scoring) {
            case 'top':
              score = values.topScore;
              break;
            case 'bottom':
              score = values.bottomScore;
              break;
            default:
              score = values.score;
              break;
          }

          if (score !== null && score !== 'Infinity' && score !== 'NaN') {
            const formattedDate = this.formatDateColumn(date, dateColumn);

            const element = map.get(formattedDate) ?? {
              metadata: numericMetadataField.label,
              [dateColumn]: formattedDate,
              buckets: [],
            };

            element.buckets.push({ count: values.docs, score });

            map.set(formattedDate, element);
          }
        }
      }
    }

    let maxBucketLength = 0;
    for (const value of map.values()) {
      result.rows.push(value);

      maxBucketLength = Math.max(maxBucketLength, value.buckets.length);
    }

    for (let index = 0; index < maxBucketLength; index++) {
      const countColumn = `count_bucket_${index}`;
      const scoreColumn = `score_bucket_${index}`;

      result.columns.push(countColumn);

      if (!isOnlyDocs) {
        result.columns.push(scoreColumn);
      }
    }

    result.rows.sort((a, b) => moment(b[dateColumn]).diff(moment(a[dateColumn])));

    return result;
  }

  public configForScatterPlotTable(
    _dashboard: Dashboard,
    widget: Widget<WidgetChartOptions>,
    response: any,
    _options: {}
  ): TableOptions {
    const result = {
      columns: ['name', 'xAxis', 'yAxis'],
      rows: [],
      options: {
        numericSuffix: '',
      },
    };

    const currentValueExtractorFn = (isAxisImpact) => {
      if (isAxisImpact) {
        return (item) => response.impactData.data.find((ipd) => ipd.label === item).score;
      }
      return (item, axis: 'x' | 'y') => {
        const currentItems = response.currentPeriodData?.items ?? response.items;

        return currentItems[item]?.[axis] ?? 0;
      };
    };

    const previousValueExtractorFn = (isAxisImpact) => {
      if (isAxisImpact) {
        return (item) => response.impactData.data.find((ipd) => ipd.label === item).previousScore;
      }
      return (item, axis: 'x' | 'y') => response.items[item][axis];
    };

    const isXAxisImpact = widget.payload.xAxis.includes('impact');
    const isYAxisImpact = widget.payload.yAxis.includes('impact');

    const currentXValueExtractor = currentValueExtractorFn(isXAxisImpact);
    const currentYValueExtractor = currentValueExtractorFn(isYAxisImpact);

    for (const key of Object.getOwnPropertyNames(response.items)) {
      const displayName = this.extractTaxonomyDisplayName(response, key);

      const row: any = {
        name: displayName.displayName,
        xAxis: currentXValueExtractor(key, 'x'),
        yAxis: currentYValueExtractor(key, 'y'),
      };

      if (response.currentPeriodData) {
        const previousXValueExtractor = previousValueExtractorFn(isXAxisImpact);
        const previousYValueExtractor = previousValueExtractorFn(isYAxisImpact);

        row.xAxisPrev = previousXValueExtractor(key, 'x');
        row.yAxisPrev = previousYValueExtractor(key, 'y');
      }

      result.rows.push(row);
    }

    if (response.currentPeriodData) {
      result.columns = ['name', 'xAxis', 'xAxisPrev', 'yAxis', 'yAxisPrev'];
    }

    return result;
  }

  public configForBreakdownTable(
    _dashboard: Dashboard,
    widget: Widget<WidgetChartOptions>,
    response: any,
    _options: {}
  ): TableOptions {
    const columns = ['name'];

    const showAsPercentage = widget.payload.display?.showAsPercentage === true;
    const { breakdownOptions, additionalBreakdownOptions } = widget.payload;

    const columnHeaders: Record<string, string> = {};

    const result = {
      columns,
      rows: [],
      options: {
        numericSuffix: '',
        columnHeaders,
      },
    };

    const ensureNumeric = (v: unknown): number => (typeof v === 'number' ? v : 0);

    const getBucketMetricValue = (
      container: unknown,
      bucketIndex: number,
      bucketName: string
    ): number => {
      if (Array.isArray(container)) {
        return ensureNumeric(container[bucketIndex]);
      }
      if (typeof container === 'object' && container !== null) {
        return ensureNumeric((container as Record<string, unknown>)[bucketName]);
      }
      return 0;
    };

    const addColumnsForBreakdown = (opts: Partial<BreakdownOptions>, prefix: string): void => {
      switch (opts.scoring) {
        case 'bucket': {
          const buckets = opts.fieldBuckets ?? [];
          buckets.forEach((_, bucketIndex) => columns.push(`${prefix}bucket_${bucketIndex}`));
          break;
        }
        case 'box': {
          if (opts.isTopShown) {
            columns.push(`${prefix}topScore`);
          }
          if (opts.isNeutralShown) {
            columns.push(`${prefix}neutralScore`);
          }
          if (opts.isBottomShown) {
            columns.push(`${prefix}bottomScore`);
          }
          break;
        }
        default: {
          if (opts.numericMetadataFieldId === 'docs') {
            columns.push(`${prefix}count`);
          } else {
            const colKey = `${prefix}score`;
            columns.push(colKey);
            columnHeaders[colKey] = FieldService.getFieldLongName(opts.numericMetadataFieldId);
          }
          break;
        }
      }
    };

    const populateRowForBreakdown = (
      row: Record<string, unknown>,
      opts: Partial<BreakdownOptions>,
      values: any,
      prefix: string
    ): void => {
      switch (opts.scoring) {
        case 'bucket': {
          const buckets = opts.fieldBuckets ?? [];
          const bucketValues: unknown = values?.bucketValues;
          const bucketScores: unknown = values?.bucketScores;
          buckets.forEach((bucket, bucketIndex) => {
            row[`${prefix}bucket_${bucketIndex}`] = showAsPercentage
              ? getBucketMetricValue(bucketScores, bucketIndex, bucket.bucketName)
              : getBucketMetricValue(bucketValues, bucketIndex, bucket.bucketName);
          });
          break;
        }
        case 'box': {
          if (opts.isTopShown) {
            row[`${prefix}topScore`] = values.topScore;
          }
          if (opts.isNeutralShown) {
            row[`${prefix}neutralScore`] = values.neutralScore;
          }
          if (opts.isBottomShown) {
            row[`${prefix}bottomScore`] = values.bottomScore;
          }
          break;
        }
        default: {
          if (opts.numericMetadataFieldId === 'docs') {
            row[`${prefix}count`] = values.score;
          } else {
            row[`${prefix}score`] = values.score;
          }
          break;
        }
      }
    };

    addColumnsForBreakdown(breakdownOptions, '');
    if (additionalBreakdownOptions) {
      addColumnsForBreakdown(additionalBreakdownOptions, 'secondary_');
    }

    const categories = Object.getOwnPropertyNames(response.items);
    for (const category of categories) {
      const metadataArray = response.items[category].metadata;
      const row: Record<string, unknown> = { name: category };

      if (Array.isArray(metadataArray)) {
        for (const [index, metadata] of metadataArray.entries()) {
          if (index === 0) {
            populateRowForBreakdown(row, breakdownOptions, metadata, '');
          } else if (index === 1 && additionalBreakdownOptions) {
            populateRowForBreakdown(row, additionalBreakdownOptions, metadata, 'secondary_');
          }
        }
      }

      result.rows.push(row);
    }

    return result;
  }

  public configForDonutTable(
    _dashboard: Dashboard,
    widget: Widget<WidgetChartOptions>,
    response: any,
    _options: {}
  ): TableOptions {
    const result = {
      columns: ['metric', 'element', 'score', 'method'],
      rows: [],
      options: {
        numericSuffix: '',
      },
    };

    const tableRows = DonutChartReportUtils.toTable(response.items, widget.payload);
    tableRows.forEach((r) => result.rows.push(r));

    return result;
  }

  public configForGaugeTable(
    _dashboard: Dashboard,
    widget: Widget<WidgetChartOptions>,
    response: any,
    _options: {}
  ): TableOptions {
    const result = {
      columns: ['metric', 'element', 'score', 'method'],
      rows: [],
      options: {
        numericSuffix: '',
      },
    };

    const tableRows = GaugeChartReportUtils.toTable(response.items, widget.payload);
    tableRows.forEach((r) => result.rows.push(r));

    return result;
  }

  private pushSentimentColumns(result: TableOptions, dashboard: Dashboard, suffix = ''): void {
    switch (dashboard.payload.sentimentScale.toString()) {
      default:
      case '':
      case '3':
        result.columns.push(`negative${suffix}`);
        result.columns.push(`neutral${suffix}`);
        result.columns.push(`positive${suffix}`);
        break;
      case '5':
        result.columns.push(`very_negative${suffix}`);
        result.columns.push(`somewhat_negative${suffix}`);
        result.columns.push(`neutral${suffix}`);
        result.columns.push(`somewhat_positive${suffix}`);
        result.columns.push(`very_positive${suffix}`);
        break;
    }
  }

  private extractTaxonomyDisplayName(
    response,
    defaultName
  ): { displayName: string; taxonomyName?: string } {
    if (!response.extra?.taxonomyNodes?.[defaultName]) {
      return { displayName: defaultName };
    }

    const taxonomyNode = response.extra.taxonomyNodes[defaultName];
    return {
      displayName: taxonomyNode.path
        ? taxonomyNode.path + ' | ' + taxonomyNode.name
        : taxonomyNode.name,
      taxonomyName: taxonomyNode.name,
    };
  }

  private formatDateColumn(ts, dateColumn) {
    return moment(ts.split(/[\+\-]\d{2}:\d{2}$/)[0])
      .tz(this.timezoneService.timezone, true)
      .format(this.DATE_INTERVAL_FORMATS[dateColumn]);
  }
}
